import type { ChangePassword, Filters, Items, ResetPassword } from '@/types';
import type { GameSessionHistory, Player, PlayerLimits, TwoFactorAuth, BetDetails } from '@/types/player';
import { AxiosError } from 'axios';
import { usePlayerStore, useAuthStore } from '@/stores';
import { useToast } from 'primevue/usetoast';
import { useI18n } from 'vue-i18n';
import { ref, computed } from 'vue';
export function usePlayer() {
    const playerStore = usePlayerStore();
    const authStore = useAuthStore();
    const i18n = useI18n({ useScope: 'global' });
    const toast = useToast();
    const loadingFlag = ref<boolean>(false);
    const loadingToggle2FAFlag = ref<boolean>(false);
    const player = computed<Player>(() => playerStore.player);
    const twoFactorAuth = ref<TwoFactorAuth>({
        qr_code: null,
        secret: null
    });
    const playerLimits = computed<PlayerLimits>(() => playerStore.playerLimits);
    const loadingPlayerFlag = computed<boolean>(() => playerStore.loadingPlayerFlag);
    const isPlayerHaveEmail = computed<boolean>(() => playerStore.isPlayerHaveEmail);
    const isKyrgyzstanResident = computed(() => {
        return player.value?.kyc_status?.foto?.is_citizen === true;
    });
    const isHideGoProfile = ref(localStorage.getItem('isHideGoProfile') === 'true');
    const isPlayer = computed<boolean>(() => playerStore.isPlayer);
    const isPlayerHaveBalance = computed<boolean>(() => isPlayer.value && player.value?.balance > 0);
    const toastChangePasswordText = 'change_password';
    const toastChangePasswordDetail = 'change_password_detail';
    const toastUpdatePlayerText = 'update_player';
    const toastUpdatePlayerDetail = 'update_player_detail';
    const toastGetPlayerText = 'get_player';
    const toastUpdateLanguageCodeText = 'update_language_code';
    const toastGetEmailConfirmationTokenText = 'get_email_confirmation_token';
    const toastConfirmEmailText = 'confirm_email';
    const toastConfirmEmailDetail = 'confirm_email_detail';
    const toastConfirmEmailTextInvalid = 'token_invalid';
    const toastGetResetPasswordTokenText = 'get_reset_password_token';
    const toastResetPasswordText = 'reset_password';
    const toastResetPasswordDetail = 'reset_password_detail';
    const toastResetPasswordTextInvalid = 'token_invalid';
    const toastGetPlayerLimitsText = 'get_player_limits';
    const toastGetFavouriteGamesText = 'get_favourite_games';
    const toastSetup2FAText = 'setup_2fa';
    const toastDisable2FADetail = 'disable_2fa_detail';
    const toastEnable2FADetail = 'enable_2fa_detail';
    async function toChangePassword(data: ChangePassword) {
        loadingFlag.value = true;
        try {
            await playerStore.awaitChangePassword(data);
            toast.add({ severity: 'success', group: 'global', summary: toastChangePasswordText, detail: toastChangePasswordDetail, life: 4000 });
        } catch (error) {
            const err = error as AxiosError;
            if (err.response?.status !== 400) {
                toast.add({ severity: 'error', group: 'global', summary: toastChangePasswordText, detail: error, life: 4000 });
            }
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toUpdateLanguageCode(code?: string) {
        try {
            const data = {
                language_code: code || String(i18n.locale.value)
            };
            await playerStore.awaitUpdatePlayer(data);
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastUpdateLanguageCodeText, detail: error, life: 4000 });
        }
    }
    async function toUpdatePlayer(data: Partial<Player>) {
        loadingFlag.value = true;
        try {
            await playerStore.awaitUpdatePlayer(data);
            // console.log('data', data);
            toast.add({ severity: 'success', group: 'global', summary: toastUpdatePlayerText, detail: toastUpdatePlayerDetail, life: 4000 });
            setTimeout(() => {
                if (data.email) {
                    toast.removeAllGroups();
                }
            }, 4000);
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastUpdatePlayerText, detail: error, life: 4000 });
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toGetPlayer() {
        try {
            await playerStore.awaitGetPlayer();
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetPlayerText, detail: error, life: 4000 });
        }
    }
    async function toGetEmailConfirmationToken(): Promise<void> {
        loadingFlag.value = true;
        try {
            await playerStore.awaitGetEmailConfirmationToken();
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetEmailConfirmationTokenText, detail: error, life: 4000 });
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toConfirmEmail(verification_token: string) {
        loadingFlag.value = true;
        try {
            await playerStore.awaitConfirmEmail(verification_token);
            toast.add({ severity: 'success', group: 'global', summary: toastConfirmEmailText, detail: toastConfirmEmailDetail, life: 4000 });
            playerStore.toUpdatePlayerField('is_email_verified', true);
        } catch (error) {
            const err = error as AxiosError;
            if (err.response?.status !== 403) {
                toast.add({ severity: 'error', group: 'global', summary: toastConfirmEmailText, detail: error, life: 4000 });
            } else {
                toast.add({ severity: 'error', group: 'global', summary: toastConfirmEmailText, detail: toastConfirmEmailTextInvalid, life: 4000 });
            }
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toGetResetPasswordToken(email: string): Promise<void> {
        loadingFlag.value = true;
        try {
            await playerStore.awaitGetResetPasswordToken(email);
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetResetPasswordTokenText, detail: error, life: 4000 });
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toResetPassword(data: ResetPassword) {
        loadingFlag.value = true;
        try {
            await playerStore.awaitResetPassword(data);
            authStore.toSetPasswordResetToken(null);
            toast.add({ severity: 'success', group: 'global', summary: toastResetPasswordText, detail: toastResetPasswordDetail, life: 4000 });
        } catch (error) {
            const err = error as AxiosError;
            if (err.response?.status !== 403) {
                toast.add({ severity: 'error', group: 'global', summary: toastResetPasswordText, detail: error, life: 4000 });
            } else {
                toast.add({ severity: 'error', group: 'global', summary: toastResetPasswordText, detail: toastResetPasswordTextInvalid, life: 4000 });
            }
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toGetPlayerLimits(): Promise<void> {
        loadingFlag.value = true;
        try {
            await playerStore.awaitGetPlayerLimits();
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetPlayerLimitsText, detail: error, life: 4000 });
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toGetFavouriteGames(): Promise<void> {
        loadingFlag.value = true;
        try {
            await playerStore.awaitGetFavouriteGames();
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetFavouriteGamesText, detail: error, life: 4000 });
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toSetup2FA(): Promise<void> {
        try {
            loadingFlag.value = true;
            const data = await playerStore.toSetup2FA();
            twoFactorAuth.value = data;
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastSetup2FAText, detail: error, life: 4000 });
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toEnable2FA(code: string): Promise<void> {
        try {
            loadingToggle2FAFlag.value = true;
            await playerStore.toEnable2FA(code);
            toast.add({ severity: 'success', group: 'global', summary: toastSetup2FAText, detail: toastEnable2FADetail, life: 4000 });
        } catch (error) {
            throw error as AxiosError;
        } finally {
            loadingToggle2FAFlag.value = false;
        }
    }
    async function toDisable2FA(code: string): Promise<void> {
        try {
            loadingToggle2FAFlag.value = true;
            await playerStore.toDisable2FA(code);
            toast.add({ severity: 'success', group: 'global', summary: toastSetup2FAText, detail: toastDisable2FADetail, life: 4000 });
        } catch (error) {
            throw error as AxiosError;
        } finally {
            loadingToggle2FAFlag.value = false;
        }
    }
    return {
        player,
        isPlayer,
        loadingFlag,
        toChangePassword,
        toUpdateLanguageCode,
        toUpdatePlayer,
        toGetPlayer,
        isPlayerHaveEmail,
        isHideGoProfile,
        toGetEmailConfirmationToken,
        toConfirmEmail,
        toGetResetPasswordToken,
        toResetPassword,
        toGetPlayerLimits,
        loadingPlayerFlag,
        playerLimits,
        twoFactorAuth,
        toSetup2FA,
        toDisable2FA,
        toEnable2FA,
        loadingToggle2FAFlag,
        isKyrgyzstanResident,
        isPlayerHaveBalance,
        toGetFavouriteGames
    };
}
export function useBetsHistory() {
    const playerStore = usePlayerStore();
    const toast = useToast();
    const betsHistory = computed<Items<GameSessionHistory>>(() => playerStore.betsHistory);
    const betDetails = computed<Items<BetDetails> | null>(() => playerStore.betDetails);
    const loadingFlag = ref<boolean>(false);
    const errorFlag = ref<boolean>(false);
    const toastGetBetsHistoryText = 'get_bets_history';
    const toastGetBetDetailsText = 'get_bet_details';
    async function toGetBetsHistory(filters: Filters): Promise<void> {
        loadingFlag.value = true;
        errorFlag.value = false;
        try {
            await playerStore.awaitGetBetsHistory(filters);
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetBetsHistoryText, detail: error, life: 4000 });
            errorFlag.value = true;
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    async function toGetBetDetails(bet_id: string, filters: Filters): Promise<void> {
        loadingFlag.value = true;
        errorFlag.value = false;
        try {
            await playerStore.awaitGetBetDetails(bet_id, filters);
        } catch (error) {
            toast.add({ severity: 'error', group: 'global', summary: toastGetBetDetailsText, detail: error, life: 4000 });
            errorFlag.value = true;
            throw error;
        } finally {
            loadingFlag.value = false;
        }
    }
    return {
        betsHistory,
        betDetails,
        loadingFlag,
        errorFlag,
        toGetBetsHistory,
        toGetBetDetails
    };
}
